import { useMemo } from "react";
import { QueryStatus, useQuery } from "@tanstack/react-query";
import { Bar, BarChart, CartesianGrid, Cell, XAxis, YAxis } from "recharts";
import { Video } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { Skeleton } from "@/components/ui/skeleton";
import { LockedContent } from "@/components/layout/locked-content";

import { message } from "@/lib/utils";
import { BrandGovernanceApiFactory, type CompetitorBenchmarkParams } from "@/apis/brand-os/brand-governance";
import type { CompetitorBenchmarkData } from "@/schema/brand-os/brand-governance";

const chartColors = ["var(--chart-1)", "var(--chart-2)", "var(--chart-3)", "var(--chart-4)", "var(--chart-5)"];

function VideoProductionComponent({ status, benchmarkData }: { status: QueryStatus; benchmarkData?: CompetitorBenchmarkData | null }) {
  switch (status) {
    case "pending":
      return <VideoProductionSkeleton />;
    case "error":
      return <LockedContent message={message} />;
    case "success":
      if (!benchmarkData?.competitors?.length) {
        return <LockedContent message={message} />;
      }
      return <VideoProductionContent data={benchmarkData} />;
  }
}

export function VideoProduction({ params }: { params: CompetitorBenchmarkParams }) {
  const { data, status } = useQuery({
    ...BrandGovernanceApiFactory.Queries.CompetitorBenchmark(params),
    enabled: Boolean(params.brand_name),
  });

  return (
    <Card className="gap-3">
      <CardHeader className="flex items-center gap-2 pb-2">
        <div className="flex size-8 items-center justify-center rounded-lg bg-blue-100">
          <Video className="size-4 text-blue-600" />
        </div>
        <CardTitle>Video Production</CardTitle>
      </CardHeader>
      <CardContent>
        <VideoProductionComponent status={status} benchmarkData={data?.data} />
      </CardContent>
    </Card>
  );
}

function VideoProductionContent({ data }: { data: CompetitorBenchmarkData }) {
  const videoProduction = data.benchmark?.video_production?.rankings;

  const chartData = useMemo(() => {
    if (!videoProduction) return [];
    return videoProduction.map(({ brand, count }) => ({ brand, value: count }));
  }, [videoProduction]);

  const chartConfig: ChartConfig = useMemo(
    () =>
      chartData.reduce(
        (acc, { brand }, i) => ({
          ...acc,
          [brand]: { label: brand, color: chartColors[i % chartColors.length] },
        }),
        {} as ChartConfig
      ),
    [chartData]
  );

  const hasPositiveCounts = chartData.some((row) => row.value > 0);
  if (!chartData.length || !hasPositiveCounts) {
    return <LockedContent message={message} className="my-8" />;
  }

  return (
    <ChartContainer config={chartConfig} className="h-56 w-full">
      <BarChart accessibilityLayer layout="vertical" data={chartData} margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
        <CartesianGrid horizontal={false} strokeDasharray="3 3" className="stroke-border/50" />
        <XAxis type="number" tickLine={false} axisLine={false} tickMargin={8} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }} />
        <YAxis type="category" dataKey="brand" tickLine={false} axisLine={false} width={70} tick={{ fill: "hsl(var(--foreground))", fontSize: 12 }} />
        <ChartTooltip
          cursor={false}
          content={
            <ChartTooltipContent
              className="min-w-44 font-semibold"
              formatter={(value) => (
                <span className="font-normal text-muted-foreground">
                  <span className="font-medium text-foreground">{value}</span> videos
                </span>
              )}
            />
          }
        />
        <Bar dataKey="value" name="Videos" radius={[0, 3, 3, 0]} barSize={20} maxBarSize={24}>
          {chartData.map((entry, index) => (
            <Cell key={`${entry.brand}-${index}`} fill={chartColors[index % chartColors.length]} />
          ))}
        </Bar>
      </BarChart>
    </ChartContainer>
  );
}

function VideoProductionSkeleton() {
  return (
    <div className="flex flex-col justify-end space-y-4 pb-8">
      {[60, 45, 75, 50, 35, 25].map((w, i) => (
        <Skeleton key={i} className="h-5 rounded-md" style={{ width: `${w}%` }} />
      ))}
    </div>
  );
}
